/**
 * A gardener's photo help (the AI allowance, D23), as the server tells it:
 * how much of it is used and the day some comes back, never an amount of
 * money.
 *
 * The allowance is rolling (the last 30 days), so there is no reset day: the
 * date is the first day the 30-day use drops back under the line as old uses
 * age out, worked out by the server in the gardener's time zone (`tz`, which
 * every AI request carries, aiDateParams) and language (`lang`).
 *
 * Two answers: the gardener's own (the profile's "Photo help this month"
 * bar, features/billing PhotoHelpBar), and the one the garden in use spends
 * (in a shared garden the owner's: the quiet "most of it used" note on the
 * photo, packet and check-up screens, AiNearNote). "This month" is the
 * gardener's word for the rolling 30 days; no screen gives a number of uses
 * or an amount of money (the subscribe page says only that the allowance is
 * generous; the specifics are in the terms on gardengameplan.com).
 *
 * Endpoints (backend; every answer also carries `success`):
 * - GET /ai-allowance?lang=&tz= → { allowance: AiAllowance | null } (the
 *   gardener's own).
 * - GET /ai-allowance/garden?lang=&tz= → the same, for the garden being
 *   tended (X-Grow-Garden decides whose).
 * - POST /ai-allowance/note-shown → {} : the near-limit note was shown to
 *   the signed-in person, kept on their account so `noteDue` stays false on
 *   every device for the window (audit A71).
 * null is a gardener with no AI at all (Free, a paused plan, AI switched off).
 *
 * The refusals themselves are not here: an AI request over the allowance
 * fails with 429 AI_LIMIT (features/billing/lib/aiLimit reads it), and
 * aiDateParams is what puts the gardener's time zone on those requests
 * (api/diagnose, api/packets, api/tray).
 *
 * Caching: features/billing/lib/useAiAllowance keeps each answer under
 * [...AI_ALLOWANCE_QUERY_KEY, scope, language] with the app's default
 * staleness, and refreshAiAllowance invalidates ['ai-allowance'] after each
 * use of the AI so the note can appear where the allowance ran low.
 *
 * @module aiAllowance
 */

import { apiClient } from './apiClient';

/** Where a gardener stands with their photo help. */
export interface AiAllowance {
  /** Whose allowance: a subscriber's (founders and the allow list too) or a trial's. */
  tier: 'subscriber' | 'trial';
  /** How much of it is used, 0 to 1. */
  share: number;
  /** A subscriber at 80% or more, not yet over: the screens' note, once. */
  near: boolean;
  /** Used up: the AI screens are refused until some comes back. */
  over: boolean;
  /** The day some comes back, in the gardener's language ("October 12"); null when not near or over, and always for a trial. */
  comesBack: string | null;
  /** The same day as YYYY-MM-DD, in the gardener's time zone. */
  comesBackOn: string | null;
  /** How many days the allowance rolls over. */
  windowDays: number;
  /** The server's word that the near-limit note should show now: near, and not shown to this person within windowDays on any device. */
  noteDue: boolean;
}

/** Whose allowance to read: the gardener's own, or the one the garden in use spends. */
export type AiAllowanceScope = 'mine' | 'garden';

/** The allowance's cache key; screens invalidate it after a use of the AI. */
export const AI_ALLOWANCE_QUERY_KEY = ['ai-allowance'] as const;

/**
 * The browser's time zone (an IANA name, "America/Chicago"), or null when it does not say.
 *
 * @returns The time zone's name.
 */
export function gardenerTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The query an AI request carries so a refusal's "Some comes back on…" date
 * is the gardener's own day: `{ tz }`, or nothing when the browser does not
 * say. A query rather than a header, so a request to another origin needs no
 * new CORS preflight.
 *
 * @returns Axios params.
 */
export function aiDateParams(): { tz?: string } {
  const tz = gardenerTimeZone();
  return tz ? { tz } : {};
}

/**
 * Where the gardener stands with their photo help.
 *
 * @param scope - Their own ('mine'), or the garden in use's ('garden').
 * @param lang - The app's language, for the date's words.
 * @returns The allowance, or null for a gardener with no AI (Free, a paused plan, switched off).
 */
export async function getAiAllowance(scope: AiAllowanceScope, lang: string): Promise<AiAllowance | null> {
  // 'garden' reads the allowance of whoever owns the garden in use (the
  // X-Grow-Garden header), which is what an AI request there spends.
  const path = scope === 'garden' ? '/ai-allowance/garden' : '/ai-allowance';
  const response = await apiClient.get<{ success: boolean; allowance: AiAllowance | null }>(path, { params: { lang, ...aiDateParams() } });
  return response.data.allowance ?? null;
}

/**
 * Tells the server the near-limit note was shown to the signed-in person, so
 * it is not due again on any of their devices for the allowance's window.
 *
 * @throws The request's error (a failure on our side is the caller's to report).
 */
export async function markAiNearNoteShown(): Promise<void> {
  await apiClient.post('/ai-allowance/note-shown');
}
